import { useState, useEffect } from 'react';
import { Lock, Save, X, AlertCircle } from 'lucide-react';
import { MentionTextarea } from './MentionTextarea';

/**
 * Fail-safe prompt field: locked/read-only by default. To change it you must
 * unlock (Edit) -> edit -> Save. While unlocked with unsaved edits the parent
 * can disable dependent Generate buttons (via onEditingChange) so nothing is
 * ever generated from stale/unsaved text. Saving only affects generations that
 * come AFTER the save.
 */
export function LockablePrompt({
  value, onSave, testId, placeholder = '', rows = 4,
  mention = false, characters = [], heightClass = '', onEditingChange,
}) {
  const [editing, setEditing] = useState(!(value ?? '').trim());
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  // Empty prompts open straight into the editor — nothing to unlock on a first write.
  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);
  useEffect(() => { if (!(value ?? '').trim()) setEditing(true); }, [value]);
  const dirty = editing && draft !== (value ?? '');
  const isEmpty = !(value ?? '').trim();
  // Only unsaved edits block dependent Generate buttons — an untouched empty field must not.
  useEffect(() => { onEditingChange?.(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try { await onSave(draft); setEditing(!(draft ?? '').trim()); }
    finally { setSaving(false); }
  };
  const cancel = () => { setDraft(value ?? ''); setEditing(isEmpty); };

  if (!editing) {
    return (
      <div data-testid={`${testId}-locked`} className="relative group">
        <div className={`ff-input w-full whitespace-pre-wrap break-words text-xs text-[#C9C9D0] pr-16 overflow-y-auto ${heightClass || 'min-h-[64px] max-h-40'}`}>
          {value ? value : <span className="text-[#55555C]">{placeholder || 'Nothing set yet'}</span>}
        </div>
        <button
          type="button"
          data-testid={`${testId}-edit`}
          onClick={() => setEditing(true)}
          className="absolute top-2 right-2 flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[#1A1A20] border border-white/10 text-[#8A8A93] hover:text-white transition-colors"
        >
          <Lock className="w-3 h-3" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testId}-editing`} className="rounded-lg ring-1 ring-[#FF4500]/40 p-0.5">
      {mention ? (
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          characters={characters}
          placeholder={placeholder}
          className={`ff-input w-full resize-y text-xs ${heightClass || 'h-24'}`}
          testId={`${testId}-textarea`}
        />
      ) : (
        <textarea
          data-testid={`${testId}-textarea`}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={placeholder}
          className={`ff-input w-full resize-y text-xs ${heightClass || 'h-24'}`}
        />
      )}
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        <button type="button" data-testid={`${testId}-save`} onClick={save} disabled={saving || (isEmpty && !draft.trim())}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#FF4500] hover:bg-[#E03C00] text-white font-medium disabled:opacity-40">
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
        </button>
        {!isEmpty && <button type="button" data-testid={`${testId}-cancel`} onClick={cancel}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-white/[0.05] text-[#8A8A93] hover:text-white">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>}
        {dirty && (
          <span className="flex items-center gap-1 text-[10px] text-[#FBBF24]">
            <AlertCircle className="w-3 h-3" /> Unsaved — save to use it in generations
          </span>
        )}
      </div>
    </div>
  );
}
